import * as React from 'react';
import { DisplayValueFormatterProps } from './ListMenuDisplayFormatters/DisplayValueFormatter';
import { TextMenuDisplayFormatter } from './ListMenuDisplayFormatters/TextMenuDisplayFormatter';

export { DisplayValueFormatterProps };

export interface MenuItemInfoBase {
  /** true for a check, false if checkable but not checked, undefined otherwise */
  checked?: boolean;
  /** If the option is disabled */
  disabled?: boolean;
  /** If the option is a divider and not a selectable option */
  isDivider?: boolean;
  /** If the option is the label for a menu item group. The group continues until the next group item or divider item. */
  isGroup?: boolean;
  /** Max height of the menu item's sub menu in px */
  subMenuMaxHeight?: number;
  /** Style overrides for the menu item's sub menu - can be used for setting the width of the menu */
  subMenuStyle?: React.CSSProperties;
  /** The value of the item - this is not the displayed string */
  value?: string;
}

/**
 * This is the original MenuItemInfo which only rendered text in the dropdown. It is used to create
 * a GenericMenuItemInfo with a Text formatter in the DropdownWidget or FlyoutListMenuWidget
 */
export interface MenuItemInfo extends MenuItemInfoBase {
  /** The displayed string of the item */
  displayValue?: string;
  /** The array of menu items in the item's submenu */
  menuItemInfos?: MenuItemInfo[];
  /**
   * A custom title for the item.
   * NOTE: The custom title will override the default title when the displayed value within the menu item is truncated.
   */
  title?: string;
}

/**
 * This provides a way to render items in the flyoutlist menu generically by taking a react component class which is rendered
 * in the menu. It differs from MenuItemInfo in that separates out the display value into a component class, a
 * keyboardAccessibleValue for keyboard accessibility, and the text for the dropdown button the menu if that is where
 * the menu was generated.
 */
export interface GenericMenuItemInfo extends MenuItemInfoBase {
  /**
   * This is a react component type (a class or function) that takes as props some data given by the dropdown
   * and rendered in the flyout list menu. This should typically have some data bound to it via closure.
   */
  displayFormatterBoundWithData: React.ComponentType<DisplayValueFormatterProps>;
  /**
   * If the item is unselectable. If set to true, this item will not be able to highlighted, navigated to, or selected. Display formatters
   * that may not be selectable could be Headers for a group of menu items.
   * @deprecated use `isGroup` for groups of menu items, to ensure the menu is exposed properly to Assistive Technology
   */
  isUnselectable?: boolean;
  /**
   * If the menu item is accessible via keyboard value (if it has text), provide the text here.
   * For example if the data bound with the display formatter is a text + icon, provide the text here
   */
  keyboardAccessibleValue?: string;
  /**  The array of menu items in the item's submenu */
  menuItemInfos?: GenericMenuItemInfo[];
  /**
   * If used in a dropdown, this is the text will appear in the dropdown button
   */
  textForDropdownButton?: string;
}

/**
 * This converts the original dropdowns with only text values to use the displayFormatter
 */
export const convertMenuItemInfoToGeneric = (basicMenuItemInfo: MenuItemInfo[], selectedValue?: string): GenericMenuItemInfo[] =>
  basicMenuItemInfo.map<GenericMenuItemInfo>(basic => ({
    checked: (!!selectedValue && basic.value === selectedValue) || basic.checked,
    disabled: basic.disabled,
    displayFormatterBoundWithData: TextMenuDisplayFormatter(basic.displayValue, basic.title),
    isDivider: basic.isDivider,
    isGroup: basic.isGroup,
    isUnselectable: false,
    keyboardAccessibleValue: basic.displayValue,
    menuItemInfos: !!basic.menuItemInfos ? convertMenuItemInfoToGeneric(basic.menuItemInfos) : undefined,
    subMenuMaxHeight: basic.subMenuMaxHeight,
    subMenuStyle: basic.subMenuStyle,
    textForDropdownButton: basic.displayValue,
    value: basic.value,
  }));
